'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import { useStore } from 'zustand';

import { useOperationalLevelTwo } from '@/feature-flags/hooks/use-operational-level-two.hook';
import type { UseLevelTwoResult } from '@/feature-flags/types/variant.types';
import { DEFAULT_LEVEL_TWO_CONFIG } from '@/feature-flags/types/variant.types';
import { authStore } from '@/store/auth.store';
import { logInfo } from '@/feature-flags/utils/logger';

const LevelTwoContext = createContext<UseLevelTwoResult>({
  config: DEFAULT_LEVEL_TWO_CONFIG,
  loading: true,
  error: null,
  isFallback: true,
  refetch: async () => {},
  variant: null,
});

interface LevelTwoProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export function LevelTwoProvider({ children, userId: userIdProp }: LevelTwoProviderProps) {
  const userIdFromStore = useStore(authStore, (state) => state.authenticatedUser?.id);
  const userId = userIdProp || userIdFromStore;
  const initTimeRef = useRef<number>(0);
  const currentUserIdRef = useRef<string | undefined>(userId);
  const prevUserIdRef = useRef<string | undefined>(userId);

  useEffect(() => {
    currentUserIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    initTimeRef.current = Date.now();

    logInfo('Level Two Provider initialized', {
      userId,
      initTimestamp: new Date(initTimeRef.current).toISOString(),
      hasUserId: !!userId,
    });

    return () => {
      const lifecycleDuration = Date.now() - initTimeRef.current;
      logInfo('Level Two Provider unmounted', {
        userId: currentUserIdRef.current,
        lifecycleDurationMs: lifecycleDuration,
      });
    };
  }, []);

  useEffect(() => {
    if (userId && userId !== prevUserIdRef.current) {
      logInfo('Level Two Provider userId changed', {
        previousUserId: prevUserIdRef.current,
        newUserId: userId,
        reason: 'user_authentication_change',
      });
      prevUserIdRef.current = userId;
    }
  }, [userId]);

  const levelTwoState = useOperationalLevelTwo(userId);

  return (
    <LevelTwoContext.Provider value={levelTwoState}>
      {children}
    </LevelTwoContext.Provider>
  );
}

export function useLevelTwoContext(): UseLevelTwoResult {
  const context = useContext(LevelTwoContext);

  if (!context) {
    throw new Error('useLevelTwoContext must be used within LevelTwoProvider');
  }

  return context;
}
